import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MASTER_DATA_SETS, type MasterDataRecordDto, type MasterDataSet } from '@complan/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/shared/format/date-format';
import { StateBadge, FlagBadge } from './AdminTags';
import { RecordEditorDialog, type MilestoneTypeOption } from './RecordEditorDialog';
import { DeleteRecordDialog } from './DeleteRecordDialog';
import {
  useCreateMasterDataRecord,
  useDeleteMasterDataRecord,
  useEditMasterDataRecord,
  useMasterDataSet,
  type MasterDataWritePayload,
} from './admin-data';
import { findingsFromError } from './admin-findings';

/**
 * The master data administration section (task 23.6, R16.1-R16.6, R16.8, R16.11).
 *
 * A set selector chooses one of the nine configurable value sets; the table lists
 * its records with create, edit, activate/deactivate and a delete request. The
 * table shows the reference count per record (R16.6) and the active/inactive state
 * (R16.5). Per-set columns appear only where the set carries them: the Platform
 * colour swatch, a priority's elevated flag, a milestone type's mandatory and
 * calendar-relevance flags.
 *
 * Activate and deactivate are ordinary edits of the `isActive` field. A delete
 * request is never carried out (R16.6): it opens the delete dialog, which states
 * the referencing count and offers deactivation — the count comes from the list
 * read's own `referencingInitiativeCount`, the same number the server's refusal
 * finding would report.
 */
export function MasterDataSection(): React.JSX.Element {
  const { t } = useTranslation();
  const [set, setSet] = useState<MasterDataSet>('platform');
  const [includeInactive, setIncludeInactive] = useState(true);

  const listQuery = useMasterDataSet(set, includeInactive);
  const createMutation = useCreateMasterDataRecord(set);
  const editMutation = useEditMasterDataRecord(set);
  const deleteMutation = useDeleteMasterDataRecord(set);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorRecord, setEditorRecord] = useState<MasterDataRecordDto | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<MasterDataRecordDto | null>(null);

  // The button that opened the editor/delete dialog, so focus can be restored to
  // it on close (R10.13's general focus-restore expectation, R18.6). Neither
  // dialog renders a `DialogTrigger` in its tree — each is a controlled `Dialog`
  // opened from a table row or the toolbar — so Radix's own trigger-based restore
  // never engages; these refs and the `onCloseAutoFocus` handlers below are the
  // explicit equivalent.
  const editorTriggerRef = useRef<HTMLButtonElement | null>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);

  const items = listQuery.data?.items ?? [];

  const milestoneTypeOptions = useMemo<readonly MilestoneTypeOption[]>(
    () =>
      set === 'milestone-type'
        ? items.map((record) => ({ id: record.id, name: record.name }))
        : [],
    [set, items],
  );

  // Findings from the last rejected create/edit surface in the editor dialog.
  const editorFindings = editorRecord
    ? findingsFromError(editMutation.error)
    : findingsFromError(createMutation.error);

  const openCreate = (trigger: HTMLButtonElement): void => {
    createMutation.reset();
    editMutation.reset();
    editorTriggerRef.current = trigger;
    setEditorRecord(null);
    setEditorOpen(true);
  };

  const openEdit = (record: MasterDataRecordDto, trigger: HTMLButtonElement): void => {
    createMutation.reset();
    editMutation.reset();
    editorTriggerRef.current = trigger;
    setEditorRecord(record);
    setEditorOpen(true);
  };

  const submitEditor = (payload: MasterDataWritePayload): void => {
    if (editorRecord) {
      editMutation.mutate(
        { id: editorRecord.id, payload },
        { onSuccess: () => setEditorOpen(false) },
      );
    } else {
      createMutation.mutate(payload, { onSuccess: () => setEditorOpen(false) });
    }
  };

  // Activate / deactivate is an edit of the `isActive` field alone (R16.5).
  const setActive = (record: MasterDataRecordDto, isActive: boolean): void => {
    editMutation.mutate({ id: record.id, payload: toPayload(set, record, isActive) });
  };

  const deactivateFromDeleteDialog = (): void => {
    if (deleteRecord) {
      editMutation.mutate(
        { id: deleteRecord.id, payload: toPayload(set, deleteRecord, false) },
        { onSuccess: () => setDeleteRecord(null) },
      );
    }
  };

  const showColour = set === 'platform';
  const showElevated = set === 'priority';
  const showMilestoneFlags = set === 'milestone-type';
  const showPosSegmentColumns = set === 'pos-segment';

  return (
    <Card className="gap-4 p-[14px]">
      <div>
        <p className="text-[13px] font-semibold text-txt">{t('admin.masterData.heading')}</p>
        <p className="text-muted-foreground mt-1 text-[11px]">{t('admin.masterData.hint')}</p>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10.5px] font-semibold uppercase text-label">
            {t('admin.masterData.setLabel')}
          </span>
          <select
            className="border-line h-8 rounded-[var(--radius-control)] border bg-panel px-2 text-[12.5px] text-txt"
            value={set}
            onChange={(event) => setSet(event.target.value as MasterDataSet)}
          >
            {MASTER_DATA_SETS.map((option) => (
              <option key={option} value={option}>
                {t(`admin.set.${option}`)}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[12px] text-txt">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            <span>{t('admin.common.showInactive')}</span>
          </label>
          <Button
            type="button"
            size="sm"
            onClick={(event) => openCreate(event.currentTarget)}
          >
            {t('admin.common.create')}
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colCode')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colName')}</TableHead>
            <TableHead className="text-right text-[10.5px] uppercase">{t('admin.masterData.table.colOrder')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colEffectiveFrom')}</TableHead>
            {showColour ? (
              <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colColour')}</TableHead>
            ) : null}
            {showElevated ? (
              <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colElevated')}</TableHead>
            ) : null}
            {showMilestoneFlags ? (
              <>
                <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colMandatory')}</TableHead>
                <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colCalendarRelevant')}</TableHead>
              </>
            ) : null}
            {showPosSegmentColumns ? (
              <>
                <TableHead className="text-right text-[10.5px] uppercase">{t('admin.masterData.table.colPosCount')}</TableHead>
                <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colSourceTimestamp')}</TableHead>
              </>
            ) : null}
            <TableHead className="text-right text-[10.5px] uppercase">{t('admin.masterData.table.colReferences')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.masterData.table.colState')}</TableHead>
            <TableHead className="text-right">
              <span className="sr-only">{t('admin.masterData.table.colActions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {listQuery.isError ? (
            <TableRow>
              <TableCell colSpan={12} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {t('admin.common.loadError')}
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {listQuery.isLoading ? t('admin.common.loading') : t('admin.masterData.table.empty')}
              </TableCell>
            </TableRow>
          ) : (
            items.map((record) => (
              <TableRow key={record.id}>
                <TableCell className="font-mono text-[12px] text-txt">{record.code}</TableCell>
                <TableCell className="text-[12.3px] font-semibold text-txt">{record.name}</TableCell>
                <TableCell className="text-right text-[12.3px] tabular-nums">{record.displayOrder}</TableCell>
                <TableCell className="text-[12.3px]">{formatDate(record.effectiveFrom)}</TableCell>
                {showColour ? (
                  <TableCell>
                    <span className="inline-flex items-center gap-2">
                      <span
                        aria-hidden
                        className="inline-block size-4 rounded-[3px] border border-[var(--line)]"
                        style={{ backgroundColor: record.colour ?? 'transparent' }}
                      />
                      <span className="font-mono text-[11px] text-muted-foreground">{record.colour}</span>
                    </span>
                  </TableCell>
                ) : null}
                {showElevated ? (
                  <TableCell>
                    <FlagBadge on={record.elevatedPriorityFlag ?? false} label={record.elevatedPriorityFlag ? t('admin.common.yes') : t('admin.common.no')} />
                  </TableCell>
                ) : null}
                {showMilestoneFlags ? (
                  <>
                    <TableCell>
                      <FlagBadge on={record.isMandatory ?? false} label={record.isMandatory ? t('admin.common.yes') : t('admin.common.no')} />
                    </TableCell>
                    <TableCell>
                      <FlagBadge on={record.calendarRelevanceFlag ?? false} label={record.calendarRelevanceFlag ? t('admin.common.yes') : t('admin.common.no')} />
                    </TableCell>
                  </>
                ) : null}
                {showPosSegmentColumns ? (
                  <>
                    <TableCell className="text-right text-[12.3px] tabular-nums">
                      {record.posCount ?? t('admin.common.none')}
                    </TableCell>
                    <TableCell className="text-[12.3px]">
                      {record.sourceTimestamp ? formatDate(record.sourceTimestamp.slice(0, 10)) : t('admin.common.none')}
                    </TableCell>
                  </>
                ) : null}
                <TableCell className="text-right text-[12.3px] tabular-nums">
                  {record.referencingInitiativeCount}
                </TableCell>
                <TableCell>
                  <StateBadge isActive={record.isActive} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      onClick={(event) => openEdit(record, event.currentTarget)}
                    >
                      {t('admin.common.edit')}
                    </Button>
                    {record.isActive ? (
                      <Button type="button" size="xs" variant="outline" onClick={() => setActive(record, false)}>
                        {t('admin.common.deactivate')}
                      </Button>
                    ) : (
                      <Button type="button" size="xs" variant="outline" onClick={() => setActive(record, true)}>
                        {t('admin.common.activate')}
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      onClick={(event) => {
                        deleteTriggerRef.current = event.currentTarget;
                        setDeleteRecord(record);
                      }}
                    >
                      {t('admin.common.delete')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <RecordEditorDialog
        set={set}
        record={editorRecord}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        milestoneTypeOptions={milestoneTypeOptions}
        onSubmit={submitEditor}
        isSubmitting={createMutation.isPending || editMutation.isPending}
        findings={editorFindings}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          editorTriggerRef.current?.focus();
        }}
      />

      <DeleteRecordDialog
        record={deleteRecord}
        open={deleteRecord !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteRecord(null);
          }
        }}
        onDeactivate={deactivateFromDeleteDialog}
        isSubmitting={editMutation.isPending || deleteMutation.isPending}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          deleteTriggerRef.current?.focus();
        }}
      />
    </Card>
  );
}

/** Builds the per-set write payload for an activate/deactivate edit, preserving all other fields. */
function toPayload(
  set: MasterDataSet,
  record: MasterDataRecordDto,
  isActive: boolean,
): MasterDataWritePayload {
  const shared = {
    code: record.code,
    name: record.name,
    displayOrder: record.displayOrder,
    effectiveFrom: record.effectiveFrom,
    isActive,
  };
  if (set === 'platform') {
    return { ...shared, colour: record.colour ?? '#000000' };
  }
  if (set === 'priority') {
    return { ...shared, elevatedPriorityFlag: record.elevatedPriorityFlag ?? false };
  }
  if (set === 'milestone-type') {
    return {
      ...shared,
      calendarRelevanceFlag: record.calendarRelevanceFlag ?? false,
      isMandatory: record.isMandatory ?? false,
      precedingMilestoneTypeId: record.precedingMilestoneTypeId,
    };
  }
  if (set === 'pos-segment') {
    return {
      ...shared,
      posCount: record.posCount ?? 0,
      sourceTimestamp: record.sourceTimestamp,
    };
  }
  return shared;
}
